import { publicPageUrl, toPublicAbsoluteUrl } from "@/lib/site-url";

export const APP_NAME = "Frequent Health";

export const DEFAULT_DESCRIPTION =
  "Frequent Health creates personalized plans for strength, endurance, and recovery — built for your life, at your speed, and powered by proprietary AI.";

type PageMeta = {
  title: string;
  description: string;
};

function parseSearch(search: string): URLSearchParams {
  try {
    return new URLSearchParams(search);
  } catch {
    return new URLSearchParams();
  }
}

function withAppName(page: string): string {
  return `${page} — ${APP_NAME}`;
}

function meta(page: string, description: string): PageMeta {
  return { title: withAppName(page), description };
}

function getClientProfileTabLabel(tab: string | null): string | null {
  if (!tab) return null;
  switch (tab) {
    case "checklist":
      return "Checklist";
    case "onboarding":
      return "Onboarding";
    case "equipment":
      return "Equipment";
    case "trainer":
      return "Trainer";
    case "workouts":
      return "Workouts";
    case "files":
      return "Files";
    case "notes":
      return "Notes";
    case "legal":
      return "Legal";
    case "overview":
      return "Overview";
    case "settings":
      return "Settings";
    default:
      return null;
  }
}

function getClientPlanTabLabel(tab: string | null): string | null {
  if (!tab) return null;
  switch (tab) {
    case "schedule":
      return "Schedule";
    case "goals":
      return "Goals";
    case "program":
      return "Program";
    default:
      return null;
  }
}

function getExploreTabLabel(tab: string | null): string | null {
  if (!tab) return null;
  switch (tab.toLowerCase()) {
    case "workouts":
    case "workout":
    case "templates":
      return "Workout Templates";
    case "knowledge":
      return "Learn";
    case "equipment":
      return "Equipment";
    case "assessments":
      return "Assessments";
    case "exercises":
      return "Exercises";
    default:
      return null;
  }
}

function getAdminTabLabel(tab: string | null): string | null {
  if (!tab) return null;
  switch (tab) {
    case "equipment":
      return "Equipment";
    case "exercises":
      return "Exercises";
    case "workouts":
      return "Workouts";
    case "programs":
      return "Programs";
    case "lessons":
      return "Lessons";
    case "users":
      return "Users";
    case "clients":
      return "Clients";
    case "contacts":
      return "Contact Submissions";
    case "settings":
      return "Settings";
    case "profile":
      return "Profile Settings";
    case "notifications":
      return "Notifications";
    case "legal":
      return "Legal";
    case "pricing":
      return "Pricing";
    case "faqs":
      return "FAQs";
    case "prompts":
      return "Prompts";
    default:
      return null;
  }
}

function getPrefixMeta(pathname: string): PageMeta | null {
  if (pathname.startsWith("/lesson/")) {
    return meta(
      "Lesson",
      "Learn from Frequent Health lessons covering training technique, recovery, and sustainable progress."
    );
  }
  if (pathname.startsWith("/assessments/")) {
    return meta(
      "Assessment",
      "Complete your Frequent Health assessment so your program can stay aligned with your goals and progress."
    );
  }
  if (pathname.startsWith("/view-workout/")) {
    return meta(
      "Workout",
      "Preview a Frequent Health workout template with guided exercises, coaching cues, and structure."
    );
  }
  return null;
}

export function getPageMeta(pathname: string, search: string): PageMeta {
  const prefixMeta = getPrefixMeta(pathname);
  if (prefixMeta) return prefixMeta;

  const params = parseSearch(search);

  switch (pathname) {
    case "/":
      return {
        title: `${APP_NAME} — Train Smarter. Level Up.`,
        description: DEFAULT_DESCRIPTION,
      };
    case "/about":
      return meta(
        "About",
        "Frequent Health combines 20+ years of expert training knowledge with proprietary AI to build adaptive programs for real life, at your speed."
      );
    case "/how-it-works":
      return meta(
        "How It Works",
        "See how Frequent Health uses AI and expert coaching to personalize your training, guide every session, and keep you progressing."
      );
    case "/pricing":
      return meta(
        "Pricing",
        "Compare Frequent Health plans and start personalized, AI-adaptive training for strength, endurance, and recovery."
      );
    case "/contact":
      return meta(
        "Contact",
        "Get in touch with Frequent Health about training, accounts, or partnerships. We’ll get back to you quickly."
      );
    case "/signup":
      return meta(
        "Sign Up",
        "Create your Frequent Health account and start a personalized training plan built around your goals, schedule, and recovery."
      );
    case "/login":
      return meta(
        "Sign In",
        "Sign in to Frequent Health to continue your personalized training, workouts, and progress tracking."
      );
    case "/reset-password":
      return meta(
        "Reset Password",
        "Reset your Frequent Health password to get back to your personalized training plan."
      );
    case "/reset-password-confirm":
      return meta(
        "Set New Password",
        "Choose a new password for your Frequent Health account."
      );
    case "/terms":
      return meta(
        "Terms of Service",
        "Read the Frequent Health Terms of Service that govern use of our training app and related services."
      );
    case "/privacy":
      return meta(
        "Privacy Policy",
        "Learn how Frequent Health collects, uses, and protects your personal information."
      );
    case "/post-login":
      return meta("Signing You In", "Signing you in to Frequent Health.");
    case "/onboarding":
      return meta(
        "Onboarding",
        "Tell Frequent Health about your goals, fitness level, and equipment so we can build your personalized plan."
      );
    case "/payment-required":
      return meta(
        "Choose a Plan",
        "Choose a Frequent Health plan to unlock personalized, AI-adaptive training."
      );
    case "/payment-verification":
      return meta("Confirming Payment", "Confirming your Frequent Health subscription.");
    case "/invited-user-setup":
      return meta(
        "Accept Invitation",
        "Finish setting up your Frequent Health account and start training."
      );
    case "/account-deactivated":
      return meta(
        "Account Deactivated",
        "This Frequent Health account is currently deactivated. Contact support if you need help."
      );
    case "/auth-degraded":
      return meta(
        "Limited Access",
        "Frequent Health sign-in is temporarily limited. Please try again shortly."
      );
    case "/client-portal":
      return meta(
        "Dashboard",
        "Your Frequent Health dashboard: today’s training, progress, and the next step in your plan."
      );
    case "/client-plan": {
      const tabLabel = getClientPlanTabLabel(params.get("tab"));
      return meta(
        tabLabel ? `Plan (${tabLabel})` : "Training Plan",
        "Follow your Frequent Health training plan, weekly schedule, and goals."
      );
    }
    case "/client-profile": {
      const tabLabel = getClientProfileTabLabel(params.get("tab"));
      return meta(
        tabLabel ? `Profile (${tabLabel})` : "Profile",
        "Manage your Frequent Health profile, equipment, trainer, and account settings."
      );
    }
    case "/explore": {
      const tabLabel = getExploreTabLabel(params.get("tab") || params.get("category"));
      return meta(
        tabLabel ?? "Explore",
        "Explore Frequent Health workouts, lessons, equipment, assessments, and exercises."
      );
    }
    case "/assessments":
      return meta(
        "Assessments",
        "Track progress with Frequent Health assessments so your program stays aligned with your goals."
      );
    case "/start-workout":
      return meta("Start Workout", "Start your next Frequent Health workout.");
    case "/workout-session":
      return meta("Workout Session", "Train with your Frequent Health workout session.");
    case "/chat":
      return meta(
        "Coach Chat",
        "Chat with your Frequent Health coach for guidance, form cues, and plan adjustments."
      );
    case "/admin": {
      const tabLabel = getAdminTabLabel(params.get("tab"));
      return meta(
        tabLabel ? `Admin (${tabLabel})` : "Admin",
        "Frequent Health admin tools for clients, programs, workouts, and settings."
      );
    }
    default:
      return meta(
        "Page Not Found",
        "This Frequent Health page could not be found. Return home to keep training smarter."
      );
  }
}

export function getDocumentTitle(pathname: string, search: string): string {
  return getPageMeta(pathname, search).title;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const OG_IMAGE_PATH = "/og-image.jpg";

export function applyDocumentMeta(pathname: string, search: string): void {
  const { title, description } = getPageMeta(pathname, search);
  const imageUrl = toPublicAbsoluteUrl(OG_IMAGE_PATH);
  const pageUrl = publicPageUrl();

  document.title = title;

  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:site_name", APP_NAME);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", pageUrl);
  upsertMeta("property", "og:image", imageUrl);
  upsertCanonical(pageUrl);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", imageUrl);
}
